// src/components/PanelHarness.tsx
import { createContext, useContext, useMemo } from "react";
import { jsx } from "react/jsx-runtime";
var PanelHarnessContext = createContext(null);
var PanelHarness = ({
  context,
  actions = {},
  events,
  children
}) => {
  const value = useMemo(() => ({
    context,
    actions,
    events: events || createMockEventEmitter()
  }), [context, actions, events]);
  return /* @__PURE__ */ jsx(PanelHarnessContext.Provider, {
    value,
    children
  });
};
var usePanelHarness = () => {
  const context = useContext(PanelHarnessContext);
  if (!context) {
    throw new Error("usePanelHarness must be used within PanelHarness");
  }
  return context;
};
var usePanelContext = () => {
  const { context } = usePanelHarness();
  return context;
};
var usePanelActions = () => {
  const { actions } = usePanelHarness();
  return actions;
};
var usePanelEvents = () => {
  const { events } = usePanelHarness();
  return events;
};
function createMockEventEmitter() {
  return {
    emit: () => {
      console.warn("PanelEventEmitter not provided to PanelHarness");
    },
    on: () => {
      console.warn("PanelEventEmitter not provided to PanelHarness");
      return () => {};
    },
    off: () => {
      console.warn("PanelEventEmitter not provided to PanelHarness");
    }
  };
}
// src/components/PanelWrapper.tsx
import React2, { useEffect, useRef, Suspense } from "react";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var DefaultFallback = () => /* @__PURE__ */ jsx2("div", {
  style: { padding: "20px", textAlign: "center", color: "#666" },
  children: "Loading panel..."
});
var DefaultErrorFallback = ({ error, reset }) => /* @__PURE__ */ jsxs("div", {
  style: {
    padding: "20px",
    color: "#d32f2f",
    border: "1px solid #d32f2f",
    borderRadius: "4px",
    margin: "10px"
  },
  children: [
    /* @__PURE__ */ jsx2("h3", {
      style: { marginTop: 0 },
      children: "Panel Error"
    }),
    /* @__PURE__ */ jsx2("p", {
      children: error.message
    }),
    /* @__PURE__ */ jsx2("button", {
      onClick: reset,
      style: {
        padding: "8px 16px",
        background: "#d32f2f",
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer"
      },
      children: "Retry"
    })
  ]
});

class PanelErrorBoundary extends React2.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("Panel error:", error, errorInfo);
  }
  reset = () => {
    this.setState({ error: null });
  };
  render() {
    if (this.state.error) {
      const Fallback = this.props.fallback;
      return /* @__PURE__ */ jsx2(Fallback, {
        error: this.state.error,
        reset: this.reset
      });
    }
    return this.props.children;
  }
}
var PanelWrapper = ({
  component: Component,
  lifecycle,
  fallback = /* @__PURE__ */ jsx2(DefaultFallback, {}),
  errorFallback = DefaultErrorFallback
}) => {
  const { context, actions, events } = usePanelHarness();
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      if (lifecycle?.onMount) {
        const result = lifecycle.onMount(context);
        if (result instanceof Promise) {
          result.catch((error) => {
            console.error("Panel onMount error:", error);
          });
        }
      }
    }
    return () => {
      if (lifecycle?.onUnmount) {
        const result = lifecycle.onUnmount(context);
        if (result instanceof Promise) {
          result.catch((error) => {
            console.error("Panel onUnmount error:", error);
          });
        }
      }
    };
  }, [context, lifecycle]);
  return /* @__PURE__ */ jsx2(PanelErrorBoundary, {
    fallback: errorFallback,
    children: /* @__PURE__ */ jsx2(Suspense, {
      fallback,
      children: /* @__PURE__ */ jsx2(Component, {
        context,
        actions,
        events
      })
    })
  });
};
// src/events/PanelEventBus.ts
class PanelEventBus {
  handlers;
  constructor() {
    this.handlers = new Map;
  }
  emit(event) {
    const typeHandlers = this.handlers.get(event.type);
    if (typeHandlers) {
      typeHandlers.forEach((handler) => {
        try {
          handler(event);
        } catch (error) {
          console.error(`Error in panel event handler for ${event.type}:`, error);
        }
      });
    }
    const wildcardHandlers = this.handlers.get("*");
    if (wildcardHandlers) {
      wildcardHandlers.forEach((handler) => {
        try {
          handler(event);
        } catch (error) {
          console.error(`Error in wildcard panel event handler:`, error);
        }
      });
    }
  }
  on(type, handler) {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set);
    }
    const handlers = this.handlers.get(type);
    handlers.add(handler);
    return () => this.off(type, handler);
  }
  off(type, handler) {
    const handlers = this.handlers.get(type);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.handlers.delete(type);
      }
    }
  }
  onAll(handler) {
    if (!this.handlers.has("*")) {
      this.handlers.set("*", new Set);
    }
    const handlers = this.handlers.get("*");
    handlers.add(handler);
    return () => this.offAll(handler);
  }
  offAll(handler) {
    const handlers = this.handlers.get("*");
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.handlers.delete("*");
      }
    }
  }
  clear() {
    this.handlers.clear();
  }
  listenerCount(type) {
    const handlers = this.handlers.get(type);
    return handlers ? handlers.size : 0;
  }
  eventNames() {
    return Array.from(this.handlers.keys()).filter((key) => key !== "*");
  }
}
// src/utils/panelRegistry.ts
class PanelRegistry {
  panels;
  loaders;
  constructor() {
    this.panels = new Map;
    this.loaders = new Map;
  }
  register(definition) {
    const { metadata, component, onMount, onUnmount, getState, stateSchema } = definition;
    if (this.panels.has(metadata.id)) {
      console.warn(`Panel ${metadata.id} is already registered. Overwriting.`);
    }
    this.panels.set(metadata.id, {
      id: metadata.id,
      metadata,
      component,
      lifecycle: {
        onMount,
        onUnmount
      },
      getState,
      stateSchema
    });
  }
  registerLazy(id, loader) {
    if (this.loaders.has(id)) {
      console.warn(`Panel loader ${id} is already registered. Overwriting.`);
    }
    this.loaders.set(id, loader);
  }
  unregister(id) {
    const hasPanel = this.panels.delete(id);
    const hasLoader = this.loaders.delete(id);
    return hasPanel || hasLoader;
  }
  async get(id) {
    if (this.panels.has(id)) {
      return this.panels.get(id);
    }
    const loader = this.loaders.get(id);
    if (loader) {
      try {
        const module = await loader();
        const entry = this.createEntryFromModule(id, module);
        this.panels.set(id, entry);
        this.loaders.delete(id);
        return entry;
      } catch (error) {
        console.error(`Failed to load panel ${id}:`, error);
        return null;
      }
    }
    return null;
  }
  getAllIds() {
    const panelIds = Array.from(this.panels.keys());
    const loaderIds = Array.from(this.loaders.keys());
    return [...new Set([...panelIds, ...loaderIds])];
  }
  getAllLoaded() {
    return Array.from(this.panels.values());
  }
  async getAllMetadata() {
    const metadata = [];
    for (const entry of this.panels.values()) {
      metadata.push(entry.metadata);
    }
    return metadata;
  }
  has(id) {
    return this.panels.has(id) || this.loaders.has(id);
  }
  clear() {
    this.panels.clear();
    this.loaders.clear();
  }
  getPanelState(id) {
    const entry = this.panels.get(id);
    if (!entry || !entry.getState) {
      return null;
    }
    try {
      return entry.getState();
    } catch (error) {
      console.error(`Failed to get state for panel ${id}:`, error);
      return null;
    }
  }
  getPanelStateSchema(id) {
    const entry = this.panels.get(id);
    return entry?.stateSchema;
  }
  getAllPanelStates() {
    const states = new Map;
    for (const [id, entry] of this.panels) {
      if (entry.getState) {
        try {
          const state = entry.getState();
          states.set(id, state);
        } catch (error) {
          console.error(`Failed to get state for panel ${id}:`, error);
        }
      }
    }
    return states;
  }
  getPanelsWithState() {
    return Array.from(this.panels.entries()).filter(([, entry]) => entry.getState !== undefined).map(([id]) => id);
  }
  createEntryFromModule(id, module) {
    return {
      id,
      metadata: module.metadata,
      component: module.default,
      lifecycle: {
        onMount: module.onMount,
        onUnmount: module.onUnmount
      },
      getState: module.getState,
      stateSchema: module.stateSchema
    };
  }
}
var globalPanelRegistry = new PanelRegistry;
// src/utils/dataSlice.ts
function createDataSlice(name, scope, data = null, options = {}) {
  return {
    scope,
    name,
    data,
    loading: options.loading ?? false,
    error: options.error ?? null,
    refresh: options.refresh ?? (async () => {})
  };
}
function createLoadingSlice(name, scope) {
  return createDataSlice(name, scope, null, { loading: true });
}
function createErrorSlice(name, scope, error) {
  return createDataSlice(name, scope, null, { error });
}
function createPanelContext(currentScope, slices = []) {
  const sliceMap = new Map(slices.map((s) => [s.name, s]));
  return {
    currentScope,
    async refresh(scope, slice) {
      const slicesToRefresh = [];
      if (slice) {
        const s = sliceMap.get(slice);
        if (s && (!scope || s.scope === scope)) {
          slicesToRefresh.push(s);
        }
      } else if (scope) {
        sliceMap.forEach((s) => {
          if (s.scope === scope) {
            slicesToRefresh.push(s);
          }
        });
      } else {
        slicesToRefresh.push(...sliceMap.values());
      }
      await Promise.all(slicesToRefresh.map((s) => s.refresh()));
    }
  };
}
// src/tools/PanelToolRegistry.ts
class PanelToolRegistry {
  tools = new Map;
  listeners = new Set;
  eventEmitter = null;
  config;
  constructor(config = {}) {
    this.config = {
      tagWeight: 3,
      descriptionWeight: 1,
      nameWeight: 5,
      debug: false,
      ...config
    };
  }
  log(message) {
    if (this.config.debug) {
      console.log(`[PanelToolRegistry] ${message}`);
    }
  }
  setEventEmitter(emitter) {
    this.eventEmitter = emitter;
    this.log("Event emitter configured");
  }
  registerPanelTools(metadata) {
    if (!metadata.tools || metadata.tools.length === 0) {
      return;
    }
    const panelId = metadata.id;
    this.log(`Registering ${metadata.tools.length} tools from panel: ${panelId}`);
    for (const tool of metadata.tools) {
      const qualifiedName = `${panelId}.${tool.name}`;
      const registeredTool = {
        ...tool,
        panelId,
        qualifiedName
      };
      this.tools.set(qualifiedName, registeredTool);
      this.log(`  Registered tool: ${qualifiedName}`);
    }
    this.notifyListeners();
  }
  unregisterPanelTools(panelId) {
    const toRemove = [];
    for (const [name, tool] of this.tools) {
      if (tool.panelId === panelId) {
        toRemove.push(name);
      }
    }
    for (const name of toRemove) {
      this.tools.delete(name);
    }
    if (toRemove.length > 0) {
      this.log(`Unregistered ${toRemove.length} tools from panel: ${panelId}`);
      this.notifyListeners();
    }
  }
  getTool(qualifiedName) {
    return this.tools.get(qualifiedName);
  }
  getAllTools() {
    return Array.from(this.tools.values());
  }
  getToolsForPanel(panelId) {
    return this.getAllTools().filter((tool) => tool.panelId === panelId);
  }
  searchTools(query, limit = 10, requiredTags) {
    const queryLower = query.toLowerCase();
    const queryWords = queryLower.split(/\s+/).filter(Boolean);
    const results = [];
    for (const tool of this.tools.values()) {
      if (requiredTags && requiredTags.length > 0) {
        const hasAllTags = requiredTags.every((tag) => tool.tags.some((t) => t.toLowerCase() === tag.toLowerCase()));
        if (!hasAllTags)
          continue;
      }
      let score = 0;
      const nameLower = tool.name.toLowerCase();
      const qualifiedLower = tool.qualifiedName.toLowerCase();
      if (nameLower === queryLower || qualifiedLower === queryLower) {
        score += this.config.nameWeight * 10;
      } else if (nameLower.startsWith(queryLower)) {
        score += this.config.nameWeight * 5;
      } else if (nameLower.includes(queryLower)) {
        score += this.config.nameWeight * 2;
      }
      for (const word of queryWords) {
        if (nameLower.includes(word)) {
          score += this.config.nameWeight;
        }
      }
      for (const tag of tool.tags) {
        const tagLower = tag.toLowerCase();
        if (tagLower === queryLower) {
          score += this.config.tagWeight * 3;
        } else if (tagLower.includes(queryLower)) {
          score += this.config.tagWeight;
        }
        for (const word of queryWords) {
          if (tagLower.includes(word)) {
            score += this.config.tagWeight;
          }
        }
      }
      const descLower = tool.description.toLowerCase();
      for (const word of queryWords) {
        if (descLower.includes(word)) {
          score += this.config.descriptionWeight;
        }
      }
      if (score > 0) {
        results.push({ tool, score });
      }
    }
    return results.sort((a, b) => b.score - a.score).slice(0, limit);
  }
  async invokeTool(qualifiedName, args, source = "tool-registry") {
    const timestamp = Date.now();
    const tool = this.tools.get(qualifiedName);
    if (!tool) {
      return {
        success: false,
        eventType: "",
        payload: args,
        timestamp,
        error: `Tool not found: ${qualifiedName}`
      };
    }
    if (!this.eventEmitter) {
      return {
        success: false,
        eventType: "",
        payload: args,
        timestamp,
        error: "No event emitter configured. Call setEventEmitter() first."
      };
    }
    const template = tool.tool_call_template;
    if (template.call_template_type !== "panel_event") {
      return {
        success: false,
        eventType: "",
        payload: args,
        timestamp,
        error: `Unsupported call template type: ${template.call_template_type}`
      };
    }
    const panelEventTemplate = template;
    const eventType = panelEventTemplate.event_type;
    try {
      const event = {
        type: eventType,
        source: panelEventTemplate.source || source,
        timestamp,
        payload: {
          ...args,
          ...panelEventTemplate.target_panel && {
            _targetPanel: panelEventTemplate.target_panel
          }
        }
      };
      this.eventEmitter.emit(event);
      this.log(`Invoked tool: ${qualifiedName} -> ${eventType}`);
      return {
        success: true,
        eventType,
        payload: args,
        timestamp
      };
    } catch (err) {
      const error = err;
      return {
        success: false,
        eventType,
        payload: args,
        timestamp,
        error: error.message
      };
    }
  }
  getToolsAsAIFunctions() {
    return this.getAllTools().map((tool) => ({
      name: tool.qualifiedName,
      description: tool.description,
      parameters: tool.inputs
    }));
  }
  getToolAsAIFunction(qualifiedName) {
    const tool = this.getTool(qualifiedName);
    if (!tool)
      return;
    return {
      name: tool.qualifiedName,
      description: tool.description,
      parameters: tool.inputs
    };
  }
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  notifyListeners() {
    for (const listener of this.listeners) {
      try {
        listener();
      } catch (err) {
        console.error("[PanelToolRegistry] Listener error:", err);
      }
    }
  }
  clear() {
    this.tools.clear();
    this.notifyListeners();
    this.log("All tools cleared");
  }
  get size() {
    return this.tools.size;
  }
}
var globalToolRegistry = null;
function getGlobalToolRegistry() {
  if (!globalToolRegistry) {
    globalToolRegistry = new PanelToolRegistry;
  }
  return globalToolRegistry;
}
function setGlobalToolRegistryEventEmitter(emitter) {
  getGlobalToolRegistry().setEventEmitter(emitter);
}
// src/drag-drop/types.ts
var PANEL_TYPES = {
  FILE_TREE: "file-tree",
  TERMINAL: "terminal",
  EDITOR: "editor",
  MARKDOWN_PREVIEW: "markdown-preview",
  FILE_PREVIEW: "file-preview"
};
var DATA_TYPES = {
  FILE_PATH: "file-path",
  DIRECTORY_PATH: "directory-path",
  TEXT_SELECTION: "text-selection",
  CODE_SYMBOL: "code-symbol"
};
var DRAG_ACTIONS = {
  VIEW: "view",
  OPEN: "open",
  INSERT_PATH: "insert-path",
  INSERT_CONTENT: "insert-content",
  REFERENCE: "reference",
  EXECUTE: "execute",
  NAVIGATE: "navigate"
};
// src/drag-drop/useDraggable.ts
import { useState, useCallback } from "react";

// src/drag-drop/utils.ts
var PANEL_DATA_MIME_TYPE = "application/x-panel-data";
function parsePanelData(e) {
  const dataStr = e.dataTransfer.getData(PANEL_DATA_MIME_TYPE);
  if (!dataStr)
    return null;
  try {
    const data = JSON.parse(dataStr);
    return validatePanelData(data) ? data : null;
  } catch (err) {
    console.warn("Failed to parse panel data:", err);
    return null;
  }
}
function validatePanelData(data) {
  return typeof data === "object" && data !== null && "sourcePanel" in data && typeof data.sourcePanel === "string" && "dataType" in data && typeof data.dataType === "string" && "primaryData" in data && typeof data.primaryData === "string";
}
function sanitizePath(path) {
  if (path.includes(" ")) {
    return `"${path}"`;
  }
  return path;
}
function createDragPreview(content, options) {
  const preview = document.createElement("div");
  preview.style.cssText = `
    position: absolute;
    top: -1000px;
    left: -1000px;
    padding: 8px 12px;
    background: ${options?.backgroundColor || "#3b82f6"};
    color: ${options?.color || "white"};
    border-radius: 6px;
    font-size: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  `;
  if (options?.icon) {
    const icon = document.createElement("span");
    icon.textContent = options.icon;
    preview.appendChild(icon);
  }
  const text = document.createElement("span");
  text.textContent = content;
  preview.appendChild(text);
  return preview;
}
function getActionForModifiers(e, suggestedActions) {
  if (e.ctrlKey || e.metaKey) {
    return "insert-path";
  }
  if (e.altKey) {
    return "reference";
  }
  if (e.shiftKey && suggestedActions && suggestedActions[1]) {
    return suggestedActions[1];
  }
  return suggestedActions?.[0];
}

// src/drag-drop/useDraggable.ts
function useDraggable(config) {
  const [isDragging, setIsDragging] = useState(false);
  const handleDragStart = useCallback((e) => {
    setIsDragging(true);
    e.dataTransfer.setData("text/plain", config.primaryData);
    const panelData = {
      sourcePanel: config.sourcePanel || "unknown",
      dataType: config.dataType,
      primaryData: config.primaryData,
      metadata: config.metadata,
      suggestedActions: config.suggestedActions,
      version: "1.0"
    };
    e.dataTransfer.setData(PANEL_DATA_MIME_TYPE, JSON.stringify(panelData));
    e.dataTransfer.effectAllowed = "copy";
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = "0.5";
    }
    if (config.dragPreview) {
      if (config.dragPreview instanceof HTMLElement) {
        e.dataTransfer.setDragImage(config.dragPreview, 0, 0);
      } else {
        const preview = createDragPreview(config.dragPreview);
        document.body.appendChild(preview);
        e.dataTransfer.setDragImage(preview, 0, 0);
        setTimeout(() => document.body.removeChild(preview), 0);
      }
    }
    config.onDragStart?.(e);
  }, [config]);
  const handleDragEnd = useCallback((e) => {
    setIsDragging(false);
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = "1";
    }
    config.onDragEnd?.(e);
  }, [config]);
  return {
    draggable: true,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    isDragging
  };
}
// src/drag-drop/useDropZone.ts
import { useState as useState2, useCallback as useCallback2 } from "react";
function useDropZone(config) {
  const [isDragOver, setIsDragOver] = useState2(false);
  const handleDragOver = useCallback2((e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    config.onDragOver?.(e);
  }, [config]);
  const handleDragEnter = useCallback2((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (config.showVisualFeedback) {
      setIsDragOver(true);
    }
  }, [config]);
  const handleDragLeave = useCallback2((e) => {
    e.preventDefault();
    e.stopPropagation();
    const relatedTarget = e.relatedTarget;
    const currentTarget = e.currentTarget;
    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      setIsDragOver(false);
    }
  }, []);
  const handleDrop = useCallback2((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const panelData = parsePanelData(e);
    if (panelData) {
      if (config.validateDrop && !config.validateDrop(panelData)) {
        console.warn("Drop validation failed", panelData);
        return;
      }
      for (const handler of config.handlers) {
        const types = Array.isArray(handler.dataType) ? handler.dataType : [handler.dataType];
        if (types.includes(panelData.dataType)) {
          handler.onDrop(panelData, e);
          return;
        }
      }
      console.warn("No handler for data type:", panelData.dataType);
    }
    const plainText = e.dataTransfer.getData("text/plain");
    if (plainText && plainText.trim() && config.onPlainTextDrop) {
      config.onPlainTextDrop(plainText, e);
    }
  }, [config]);
  return {
    onDragOver: handleDragOver,
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
    isDragOver
  };
}
export {
  validatePanelData,
  usePanelHarness,
  usePanelEvents,
  usePanelContext,
  usePanelActions,
  useDropZone,
  useDraggable,
  setGlobalToolRegistryEventEmitter,
  sanitizePath,
  parsePanelData,
  globalPanelRegistry,
  getGlobalToolRegistry,
  getActionForModifiers,
  createPanelContext,
  createLoadingSlice,
  createErrorSlice,
  createDragPreview,
  createDataSlice,
  PanelWrapper,
  PanelToolRegistry,
  PanelRegistry,
  PanelHarness,
  PanelEventBus,
  PANEL_TYPES,
  PANEL_DATA_MIME_TYPE,
  DRAG_ACTIONS,
  DATA_TYPES
};
